/* AOP Budget — Summary tab: three stacked tables (Next Year selected
   version, Current Year Live EAC, backend-computed Variance), one row per
   business type + a Total row, columns Revenue/Direct Labor/Subcontracts/
   ODC/Gross Profit. See APIIntegrationFiles/AOP_BUDGET_API.md — business
   types are NOT a fixed set (the API's own note: "do not assume exactly 4
   sections/rows"), so rows render in whatever order/count the response
   gives, with the trailing "Total" row split out and pinned last through
   any column sort. Table chrome (header colors, sort chevrons,
   click-to-cycle headers) is the same system Waterfall EAC and Monthly DFR
   use. */
import React, { useEffect, useMemo, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getAOPBudgetSummaryAPI, getVersionsAPI } from "../../../../api/reports-api";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
  dynamicColSize,
} from "../waterfall-eac/waterfallTableUtils";
import { TABLE_FONT, TH_BG, TH_TEXT, TH_BORDER, CELL_FG } from "../../reportTableConfig";
import { METRICS, fmtMoney, splitTotalRow, mapAOPBudgetSummary } from "./aopBudgetUtils";

const FONT = TABLE_FONT;
const NAME_W = 220;
const COL_W = 150;
const NEG = "#D64550";

const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #EBEBEB",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04),0 4px 16px rgba(0,0,0,0.03)",
  overflow: "hidden",
  marginBottom: 20,
};
const HEAD = {
  padding: "14px 20px",
  borderBottom: "1px solid #E2E8F0",
  fontSize: 14,
  fontWeight: 700,
  color: "#111827",
  fontFamily: FONT,
};
const VP = { display: "inline-flex", alignItems: "center", border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden", background: "#fff", height: 32 };
const VP_L = { padding: "0 10px", height: "100%", display: "flex", alignItems: "center", background: "rgba(99,102,241,0.08)", borderRight: "1px solid #E2E8F0", fontSize: 11, fontWeight: 700, color: "#3730A3", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: FONT, whiteSpace: "nowrap" };
const VP_S = { border: "none", outline: "none", background: "#fff", fontSize: 12, color: "#374151", padding: "0 8px", height: "100%", cursor: "pointer", fontFamily: FONT, minWidth: 130 };
const thStyle = (align) => ({
  position: "sticky",
  top: 0,
  background: TH_BG,
  color: TH_TEXT,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  padding: "10px 12px",
  textAlign: align,
  border: "none",
  boxShadow: `inset 0 -1px 0 ${TH_BORDER}`,
  cursor: "pointer",
  ...dynamicColSize(align === "left" ? NAME_W : COL_W),
});
const tdName = (bold) => ({
  padding: "8px 12px",
  fontSize: 12.5,
  fontWeight: bold ? 700 : 500,
  color: "#111827",
  borderBottom: "1px solid #E2E8F0",
  ...dynamicColSize(NAME_W),
});
const tdVal = (v, bold) => ({
  padding: "8px 12px",
  fontSize: 12.5,
  fontWeight: bold ? 700 : 400,
  textAlign: "right",
  color: v != null && Number(v) < 0 ? NEG : CELL_FG,
  borderBottom: "1px solid #E2E8F0",
  fontVariantNumeric: "tabular-nums",
  ...dynamicColSize(COL_W),
});

/** One sortable table — Business Type × Metric — with its own sort state,
 *  independent from the other two blocks. `rows` excludes the Total row
 *  (split out by the caller via splitTotalRow); Total always renders last,
 *  immune to whatever sort is applied to the business-type rows above it. */
function BusinessTypeTable({ title, rows, totalRow, headerRight }) {
  const [sortKeys, setSortKeys] = useState([]);
  const handleSort = (colKey) => setSortKeys((prev) => toggleSortKey(prev, colKey));
  const onThClick = createSortThClick(handleSort);
  const getSortValue = (row, key) => (key === "businessType" ? row.businessType ?? "" : Number(row[key]) || 0);
  const getSortLabel = (key) => (key === "businessType" ? "Business Type" : METRICS.find((m) => m.key === key)?.label || key);

  const sortedRows = sortKeys.length === 0 ? rows : [...rows].sort((a, b) => compareBySortKeys(a, b, sortKeys, getSortValue));

  return (
    <div style={CARD}>
      <div style={HEAD}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <span>{title}</span>
          {headerRight}
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: NAME_W + METRICS.length * COL_W, fontFamily: FONT }}>
          <thead>
            <tr>
              <th style={thStyle("left")} onClick={onThClick("businessType")}>
                <WaterfallThLabel label="Business Type" colKey="businessType" sortKeys={sortKeys} getLabel={getSortLabel} align="left" textColor={TH_TEXT} />
              </th>
              {METRICS.map((m) => (
                <th key={m.key} style={thStyle("right")} onClick={onThClick(m.key)}>
                  <WaterfallThLabel label={m.label} colKey={m.key} sortKeys={sortKeys} getLabel={getSortLabel} align="right" textColor={TH_TEXT} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 && !totalRow && (
              <tr>
                <td colSpan={METRICS.length + 1} style={{ padding: 24, textAlign: "center", color: "#94A3B8", fontSize: 12.5 }}>
                  No data for the selected filters.
                </td>
              </tr>
            )}
            {sortedRows.map((r, i) => (
              <tr key={r.businessType ?? i}>
                <td style={tdName(false)}>{r.businessType}</td>
                {METRICS.map((m) => (
                  <td key={m.key} style={tdVal(r[m.key])}>{fmtMoney(r[m.key])}</td>
                ))}
              </tr>
            ))}
            {totalRow && (
              <tr>
                <td style={{ ...tdName(true), background: "#F0FFF4" }}>{totalRow.businessType || "Total"}</td>
                {METRICS.map((m) => (
                  <td key={m.key} style={{ ...tdVal(totalRow[m.key], true), background: "#F0FFF4" }}>{fmtMoney(totalRow[m.key])}</td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Summary({ filters, version, onVersionChange, onMeta }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    getVersionsAPI(filters?.year || "").then((res) => {
      setVersions(res?.data || []);
      onVersionChange?.("");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.year]);

  const apiFilterKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAOPBudgetSummaryAPI(filters, version)
      .then((res) => {
        if (cancelled) return;
        if (res?.data) {
          const mapped = mapAOPBudgetSummary(res);
          setData(mapped);
          onMeta?.({
            currentYear: mapped.currentYear,
            nextYear: mapped.nextYear,
            nextYearVersion: mapped.nextYearVersion,
          });
        } else {
          setError(res?.message || "Failed to load data");
          setData(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load data");
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFilterKey, version]);

  const vOpts = useMemo(
    () => [
      { value: "", label: "Live EAC" },
      ...(versions || []).filter((v) => v.key !== "liveEac").map((v) => ({ value: v.key, label: v.name })),
    ],
    [versions],
  );

  if (loading) return <Loading />;
  if (error) {
    return (
      <div style={{ padding: "40px 24px", textAlign: "center", color: "#D64550", fontSize: 13, fontFamily: FONT }}>
        {error}
      </div>
    );
  }
  if (!data) return null;

  const table1 = splitTotalRow(data.table1);
  const table2 = splitTotalRow(data.table2);
  const varianceSplit = splitTotalRow(data.variance);

  return (
    <div style={{ fontFamily: FONT, paddingBottom: 24 }}>
      <BusinessTypeTable
        title={`Next Year (FY ${data.nextYear ?? "—"}) — Selected Version: ${data.nextYearVersion?.name ?? "—"}`}
        rows={table1.rows}
        totalRow={table1.total}
        headerRight={
          <div style={VP}>
            <span style={VP_L}>Version</span>
            <select style={VP_S} value={version} onChange={(e) => onVersionChange?.(e.target.value)}>
              {vOpts.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        }
      />
      <BusinessTypeTable
        title={`Current Year (FY ${data.currentYear ?? "—"}) — Open Version: ${data.currentYearVersion?.name ?? "Live EAC"}`}
        rows={table2.rows}
        totalRow={table2.total}
      />
      <BusinessTypeTable
        title="Variance (Next Year − Current Year)"
        rows={varianceSplit.rows}
        totalRow={varianceSplit.total}
      />
    </div>
  );
}
